import PDFPage from './PDFPage'
import type { ReportData, BrandingConfig } from '@/types'
import { formatCurrency } from '@/utils/calculations'

interface GrowthProjectionPageProps {
  data: ReportData
  branding: BrandingConfig
  pageNumber: number
  totalPages: number
}

export default function GrowthProjectionPage({
  data,
  branding,
  pageNumber,
  totalPages,
}: GrowthProjectionPageProps) {
  const isLight = branding.theme === 'light'
  const cardBg = isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'
  const cardBorder = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'
  const mutedColor = isLight ? '#64748b' : '#a8a5a0'

  // Select key milestones from timeline
  const milestones = [
    data.timeline[0],
    data.timeline[Math.min(5, data.timeline.length - 1)],
    data.timeline[Math.min(10, data.timeline.length - 1)],
    data.timeline[Math.min(15, data.timeline.length - 1)],
    data.timeline[data.timeline.length - 1],
  ].filter((v, i, arr) => arr.indexOf(v) === i) // Remove duplicates

  return (
    <PDFPage
      pageNumber={pageNumber}
      totalPages={totalPages}
      title="Growth Projection"
      subtitle={`${data.years}-Year Outlook`}
      branding={branding}
    >
      {/* Projection Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <ProjectionCard
          label="5-Year Value"
          withAdvice={data.timeline[Math.min(5, data.timeline.length - 1)]?.withAdvice || 0}
          withoutAdvice={data.timeline[Math.min(5, data.timeline.length - 1)]?.withoutAdvice || 0}
          branding={branding}
          cardBg={cardBg}
          cardBorder={cardBorder}
        />
        <ProjectionCard
          label="10-Year Value"
          withAdvice={data.timeline[Math.min(10, data.timeline.length - 1)]?.withAdvice || 0}
          withoutAdvice={data.timeline[Math.min(10, data.timeline.length - 1)]?.withoutAdvice || 0}
          branding={branding}
          cardBg={cardBg}
          cardBorder={cardBorder}
        />
        <ProjectionCard
          label={`${data.years}-Year Value`}
          withAdvice={data.results.withAdvice}
          withoutAdvice={data.results.withoutAdvice}
          branding={branding}
          cardBg={cardBg}
          cardBorder={cardBorder}
          highlight
        />
      </div>

      {/* Growth Timeline Visual */}
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>
          Portfolio Growth Comparison
        </h3>

        {/* Bar comparison for each milestone */}
        {milestones.map((point) => (
          <div key={point.year} style={{ marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                marginBottom: '6px',
              }}
            >
              <span>Year {point.year}</span>
              <span style={{ color: branding.primaryColor, fontWeight: 500 }}>
                +{formatCurrency(point.valueAdded)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    height: '24px',
                    background: branding.primaryColor,
                    borderRadius: '4px',
                    width: `${(point.withAdvice / data.results.withAdvice) * 100}%`,
                    minWidth: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '8px',
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#1a1f2e',
                  }}
                >
                  {formatCurrency(point.withAdvice)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    height: '24px',
                    background: mutedColor,
                    borderRadius: '4px',
                    width: `${(point.withoutAdvice / data.results.withAdvice) * 100}%`,
                    minWidth: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '8px',
                    fontSize: '11px',
                    fontWeight: 500,
                    color: isLight ? '#ffffff' : '#1a1f2e',
                  }}
                >
                  {formatCurrency(point.withoutAdvice)}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Legend */}
        <div style={{ display: 'flex', gap: '24px', marginTop: '16px', fontSize: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                background: branding.primaryColor,
              }}
            />
            <span>With Advice</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                background: mutedColor,
              }}
            />
            <span>Without Advice</span>
          </div>
        </div>
      </div>

      {/* Value Added Highlight */}
      <div
        style={{
          background: `linear-gradient(135deg, ${branding.primaryColor}15 0%, ${branding.secondaryColor}15 100%)`,
          border: `1px solid ${branding.primaryColor}30`,
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '13px', color: branding.primaryColor, marginBottom: '8px' }}>
          Total Value of Advice Over {data.years} Years
        </div>
        <div
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: '36px',
            fontWeight: 600,
            color: branding.primaryColor,
          }}
        >
          {formatCurrency(data.results.valueAdded)}
        </div>
        <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '8px' }}>
          That&apos;s {data.results.percentageGain.toFixed(1)}% more than without advice
        </div>
      </div>
    </PDFPage>
  )
}

function ProjectionCard({
  label,
  withAdvice,
  withoutAdvice,
  branding,
  cardBg,
  cardBorder,
  highlight = false,
}: {
  label: string
  withAdvice: number
  withoutAdvice: number
  branding: BrandingConfig
  cardBg: string
  cardBorder: string
  highlight?: boolean
}) {
  const valueAdded = withAdvice - withoutAdvice

  return (
    <div
      style={{
        background: highlight
          ? `linear-gradient(135deg, ${branding.primaryColor}15 0%, ${branding.secondaryColor}15 100%)`
          : cardBg,
        border: `1px solid ${highlight ? branding.primaryColor + '40' : cardBorder}`,
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '8px' }}>{label}</div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 600,
          color: highlight ? branding.primaryColor : 'inherit',
        }}
      >
        {formatCurrency(withAdvice)}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: branding.primaryColor,
          marginTop: '4px',
        }}
      >
        +{formatCurrency(valueAdded)} value added
      </div>
    </div>
  )
}
